import api from './api';

// ============================
// FRONTEND CACHE SİSTEMİ
// ============================

const serviceCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika
const CACHE_SIZE_LIMIT = 100; // Max 100 cache entry

// Cache helper fonksiyonları
const getCacheKey = (url, params = {}) => {
    // Parametreleri sırala ve string'e çevir (tutarlı key için)
    const sortedParams = Object.keys(params).sort().reduce((result, key) => {
        result[key] = params[key];
        return result;
    }, {});
    return `${url}_${JSON.stringify(sortedParams)}`;
};

const getCachedData = (key) => {
    const cached = serviceCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log(`🎯 Frontend cache hit: ${key.substring(0, 50)}...`);
        return cached.data;
    }

    // Expired cache'i temizle
    if (cached) {
        serviceCache.delete(key);
    }
    return null;
};

const setCachedData = (key, data) => {
    // Cache boyut kontrolü
    if (serviceCache.size >= CACHE_SIZE_LIMIT) {
        // En eski entry'yi sil (LRU)
        const firstKey = serviceCache.keys().next().value;
        serviceCache.delete(firstKey);
        console.log(`🗑️ Cache size limit reached, removed: ${firstKey.substring(0, 30)}...`);
    }

    serviceCache.set(key, {
        data,
        timestamp: Date.now()
    });
    console.log(`💾 Frontend cache set: ${key.substring(0, 50)}... (${serviceCache.size}/${CACHE_SIZE_LIMIT})`);
};

// Cache temizleme
const clearCache = () => {
    serviceCache.clear();
    console.log('🧹 Frontend cache cleared');
};

// Cache istatistikleri
const getCacheStats = () => {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    serviceCache.forEach(entry => {
        if (now - entry.timestamp < CACHE_DURATION) {
            validEntries++;
        } else {
            expiredEntries++;
        }
    });

    return {
        totalEntries: serviceCache.size,
        validEntries,
        expiredEntries,
        cacheHitRate: localStorage.getItem('cache_hits') || 0,
        cacheMissRate: localStorage.getItem('cache_misses') || 0
    };
};

// ============================
// OPTIMIZE EDİLMİŞ SERVICE FUNCTIONS
// ============================

// Araçları listeleme - CACHE'Lİ
export const getVehicles = async (filters = {}) => {
    try {
        const cacheKey = getCacheKey('/vehicles', filters);
        const cachedResult = getCachedData(cacheKey);

        if (cachedResult) {
            // Cache hit stats
            const hits = parseInt(localStorage.getItem('cache_hits') || '0') + 1;
            localStorage.setItem('cache_hits', hits.toString());
            return cachedResult;
        }

        // Cache miss stats
        const misses = parseInt(localStorage.getItem('cache_misses') || '0') + 1;
        localStorage.setItem('cache_misses', misses.toString());

        const startTime = performance.now();

        // Boş parametreleri filtrele (API'ya gereksiz parametre gönderme)
        const cleanFilters = Object.entries(filters)
            .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
            .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

        const queryString = new URLSearchParams(cleanFilters).toString();
        const response = await api.get(`/vehicles?${queryString}`);

        const endTime = performance.now();
        console.log(`⚡ getVehicles API call: ${Math.round(endTime - startTime)}ms`);

        // Response'u cache'le
        setCachedData(cacheKey, response.data);
        return response.data;
    } catch (error) {
        console.error('❌ Araçları listeleme hatası:', error);
        throw error;
    }
};

// Araç detayları - CACHE'Lİ
export const getVehicleById = async (id) => {
    try {
        const cacheKey = getCacheKey(`/vehicles/${id}`);
        const cachedResult = getCachedData(cacheKey);

        if (cachedResult) {
            return cachedResult;
        }

        const startTime = performance.now();
        const response = await api.get(`/vehicles/${id}`);
        const endTime = performance.now();

        console.log(`⚡ getVehicleById API call: ${Math.round(endTime - startTime)}ms`);

        // Cloudinary default resimlerini filtrele
        if (response.data && response.data.success && response.data.vehicle) {
            const vehicle = response.data.vehicle;

            // Default Cloudinary resimlerini temizle
            if (vehicle.images && Array.isArray(vehicle.images)) {
                vehicle.images = vehicle.images.filter(img =>
                    img && !img.includes('default_zxlbnq.webp'));
            }

            if (vehicle.imageUrl && vehicle.imageUrl.includes('default_zxlbnq.webp')) {
                vehicle.imageUrl = '/uploads/vehicles/default.jpg';
            }
        }

        // Detay sayfası için daha uzun cache (10 dakika)
        const detailCacheKey = getCacheKey(`/vehicles/detail/${id}`);
        serviceCache.set(detailCacheKey, {
            data: response.data,
            timestamp: Date.now()
        });

        setCachedData(cacheKey, response.data);
        return response.data;
    } catch (error) {
        console.error('❌ Araç detaylarını alma hatası:', error);
        throw error;
    }
};

// Kullanıcının araçları - CACHE'SİZ (güncel olmalı)
export const getMyVehicles = async () => {
    try {
        const startTime = performance.now();
        const response = await api.get(`/vehicles/my/vehicles`);
        const endTime = performance.now();

        console.log(`⚡ getMyVehicles API call: ${Math.round(endTime - startTime)}ms`);
        return response.data;
    } catch (error) {
        console.error('❌ Araçlarımı getirme hatası:', error);
        throw error;
    }
};

// ============================
// CRUD OPERATIONS - CACHE TEMİZLEYİCİ
// ============================

// Yeni araç oluşturma
// ✅ DÜZELTILMIŞ createVehicle - FormData desteği eklendi
export const createVehicle = async (vehicleData) => {
    try {
        console.log('🚗 Yeni araç oluşturuluyor...');

        // ✅ FIX: FormData vs JSON kontrolü
        const isFormData = vehicleData instanceof FormData;

        if (isFormData) {
            console.log('📁 FormData algılandı - Multipart upload');

            // FormData için özel headers - Content-Type otomatik set edilsin
            const response = await api.post('/vehicles', vehicleData, {
                headers: {
                    // Content-Type'ı belirtme, browser otomatik boundary ekleyecek
                },
                timeout: 30000 // 30 saniye timeout (büyük dosyalar için)
            });

            console.log('✅ FormData başarıyla gönderildi');

            // Cache'i temizle çünkü yeni araç eklendi
            clearCache();
            console.log('🧹 Cache cleared after vehicle creation');

            return response.data;

        } else {
            console.log('📝 JSON algılandı - Standard upload');

            // Normal JSON gönderimi
            const response = await api.post('/vehicles', vehicleData);

            // Cache'i temizle çünkü yeni araç eklendi
            clearCache();
            console.log('🧹 Cache cleared after vehicle creation');

            return response.data;
        }

    } catch (error) {
        console.error('❌ Araç oluşturma hatası:', error);

        // ✅ FIX: Daha detaylı error handling
        if (error.response) {
            // Server response var ama error
            console.error('Server Error Response:', error.response.data);
            console.error('Status:', error.response.status);

            // ✅ FIX: Response data'sını preserve et
            const preservedError = new Error(error.response.data?.message || `Server error: ${error.response.status}`);
            preservedError.response = error.response; // ✅ Response'u kaybet
            throw preservedError;
        } else if (error.request) {
            // Request gönderildi ama response yok
            console.error('Network Error - No response received');
            throw new Error('Ağ hatası - Sunucuya ulaşılamıyor');
        } else {
            // Request oluşturulurken hata
            console.error('Request Setup Error:', error.message);
            throw new Error('İstek hazırlanırken hata: ' + error.message);
        }
    }
};

// Araç güncelleme
export const updateVehicle = async (id, vehicleData) => {
    try {
       
        // Cloudinary URL'lerini optimize et
        if (vehicleData.images && Array.isArray(vehicleData.images)) {
            vehicleData.images = vehicleData.images.map(img => {
                if (!img) return img;

                if (img.includes('cloudinary.com')) {
                    return img; // Cloudinary URL'si olduğu gibi kalsın
                } else if (img.startsWith('http')) {
                    // Tam URL'leri göreceli yollara dönüştür
                    const url = new URL(img);
                    return url.pathname;
                }
                return img;
            }).filter(Boolean);
        }

        // Ana resim URL'si için de aynı işlem
        if (vehicleData.imageUrl) {
            if (vehicleData.imageUrl.includes('cloudinary.com')) {
                // Cloudinary URL'si olduğu gibi kalsın
            } else if (vehicleData.imageUrl.startsWith('http')) {
                const url = new URL(vehicleData.imageUrl);
                vehicleData.imageUrl = url.pathname;
            }
        }

        const response = await api.put(`/vehicles/${id}`, vehicleData);

        // İlgili cache entry'leri temizle
        const keysToDelete = [];
        serviceCache.forEach((_, key) => {
            if (key.includes('/vehicles') || key.includes(id)) {
                keysToDelete.push(key);
            }
        });

        keysToDelete.forEach(key => {
            serviceCache.delete(key);
        });

        console.log(`🧹 Cleared ${keysToDelete.length} cache entries after vehicle update`);
        return response.data;
    } catch (error) {
        console.error('❌ Araç güncelleme hatası - DETAYLI:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            config: {
                url: error.config?.url,
                method: error.config?.method
            }
        });

        // Backend'den gelen detaylı hata mesajını yakala
        if (error.response?.data) {
            const backendError = error.response.data;
            console.error('🔍 Backend Error Details:', backendError);

            // Meaningful error message oluştur
            const errorMessage = backendError.message ||
                backendError.error ||
                `Server Error: ${error.response.status}`;

            throw new Error(errorMessage);
        }

        // Network hatası vs
        if (error.request) {
            throw new Error('Ağ hatası - Sunucuya ulaşılamıyor');
        }

        // Diğer hatalar
        throw new Error(error.message || 'Bilinmeyen hata oluştu');
    }
};

// Araç silme
export const deleteVehicle = async (id) => {
    try {
        console.log(`🗑️ Araç siliniyor: ID=${id}`);
        const response = await api.delete(`/vehicles/${id}`);

        // Tüm vehicle cache'ini temizle
        clearCache();
        console.log('🧹 Cache cleared after vehicle deletion');

        return response.data;
    } catch (error) {
        console.error('❌ Araç silme hatası:', error);
        throw error;
    }
};

// ============================
// CACHE MANAGEMENT EXPORTS
// ============================

export const vehicleCacheManager = {
    clear: clearCache,
    size: () => serviceCache.size,
    keys: () => Array.from(serviceCache.keys()),
    stats: getCacheStats,

    // Manuel cache temizleme (belirli pattern için)
    clearPattern: (pattern) => {
        const keysToDelete = [];
        serviceCache.forEach((_, key) => {
            if (key.includes(pattern)) {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach(key => serviceCache.delete(key));
        console.log(`🧹 Cleared ${keysToDelete.length} cache entries matching pattern: ${pattern}`);
    },

    // Cache sıcaklığı (hangi entry'ler sık kullanılıyor)
    getHotEntries: () => {
        const entries = Array.from(serviceCache.entries()).map(([key, value]) => ({
            key: key.substring(0, 50),
            age: Math.round((Date.now() - value.timestamp) / 1000),
            size: JSON.stringify(value.data).length
        }));
        return entries.sort((a, b) => a.age - b.age);
    }
};

// vehicleService.js dosyasının en sonuna şu satırları ekleyin:

// Development modunda cache manager'ı global olarak kullanılabilir yap
if (process.env.NODE_ENV === 'development') {
    window.vehicleCacheManager = vehicleCacheManager;
    window.vehicleServiceCache = serviceCache;

    // Debug fonksiyonları
    window.debugVehicleService = {
        clearCache: () => {
            clearCache();
            console.log('🧹 Cache manually cleared');
        },

        showStats: () => {
            const stats = getCacheStats();
            console.table(stats);
            return stats;
        },

        showHotEntries: () => {
            const hot = vehicleCacheManager.getHotEntries();
            console.table(hot);
            return hot;
        },

        testCache: async () => {
            console.log('🧪 Testing cache...');

            // İlk çağrı
            const start1 = performance.now();
            await getVehicles();
            const end1 = performance.now();
            console.log(`First call: ${Math.round(end1 - start1)}ms`);

            // İkinci çağrı (cache'den)
            const start2 = performance.now();
            await getVehicles();
            const end2 = performance.now();
            console.log(`Second call (cached): ${Math.round(end2 - start2)}ms`);

            console.log(`Speed improvement: ${Math.round((end1 - start1) / (end2 - start2))}x faster`);
        }
    };

}

export default {
    getVehicles,
    getMyVehicles,
    getVehicleById,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    vehicleCacheManager
};